import { Briefcase, Crown, Heart, User, Users } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/cn";

type ContactCategory = "family" | "friend" | "business" | "vip" | "other";

interface CategoryBadgeProps {
  category: ContactCategory;
  size?: "sm" | "md";
}

export const CATEGORY_CONFIG: Record<
  ContactCategory,
  {
    icon: LucideIcon;
    label: string;
    labelAm: string;
    help: string;
    helpAm: string;
    bgClass: string;
    textClass: string;
  }
> = {
  family: {
    icon: Users,
    label: "Family",
    labelAm: "ቤተሰብ",
    help: "Close family and relatives",
    helpAm: "ቤተሰብና የቅርብ ዘመዶች",
    bgClass: "bg-purple-500/20",
    textClass: "text-purple-300",
  },
  friend: {
    icon: Heart,
    label: "Friend",
    labelAm: "ጓደኛ",
    help: "Friends and regular people",
    helpAm: "ጓደኞችና መደበኛ ሰዎች",
    bgClass: "bg-green-500/20",
    textClass: "text-green-300",
  },
  business: {
    icon: Briefcase,
    label: "Business",
    labelAm: "ንግድ",
    help: "Customers, suppliers, partners",
    helpAm: "ደንበኞች፣ አቅራቢዎች፣ አጋሮች",
    bgClass: "bg-blue-500/20",
    textClass: "text-blue-300",
  },
  vip: {
    icon: Crown,
    label: "VIP",
    labelAm: "VIP",
    help: "Top customers and priority contacts",
    helpAm: "ዋና ደንበኞችና ቅድሚያ የሚያገኙ እውቂያዎች",
    bgClass: "bg-amber-500/20",
    textClass: "text-amber-300",
  },
  other: {
    icon: User,
    label: "Other",
    labelAm: "ሌላ",
    help: "Anyone not sure yet",
    helpAm: "ገና ያልተመደበ ማንኛውም ሰው",
    bgClass: "bg-slate-500/20",
    textClass: "text-[var(--color-text-secondary)]",
  },
};

export function CategoryBadge({ category, size = "md" }: CategoryBadgeProps) {
  const config = CATEGORY_CONFIG[category];
  const Icon = config.icon;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-[var(--radius-full)] font-medium",
        config.bgClass,
        config.textClass,
        size === "sm" ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-xs",
      )}
    >
      <Icon className={size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5"} aria-hidden="true" />
      {config.label}
    </span>
  );
}

export type { ContactCategory };
